export type CalendarParticipantRsvpState = 'needs_action' | 'accepted' | 'declined' | 'tentative'

export interface CalendarExternalParticipant {
  email: string
  display_name?: string
  rsvp_state?: CalendarParticipantRsvpState
}

export function participantEmail(value: unknown): string {
  if (typeof value === 'string') {
    return value.trim()
  }

  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>
    return typeof record.email === 'string' ? record.email.trim() : ''
  }

  return ''
}

export function participantDisplayLabel(value: unknown): string {
  if (typeof value === 'string') {
    return value
  }

  if (value && typeof value === 'object') {
    const record = value as CalendarExternalParticipant
    const name = record.display_name?.trim()
    const email = record.email?.trim()
    return name || email || ''
  }

  return ''
}

export function normalizeExternalParticipantDetails(value: unknown): CalendarExternalParticipant[] {
  if (!Array.isArray(value)) {
    return []
  }

  const seen = new Set<string>()
  const result: CalendarExternalParticipant[] = []

  for (const item of value) {
    const email = participantEmail(item)
    if (!email || seen.has(email.toLowerCase())) {
      continue
    }

    seen.add(email.toLowerCase())
    if (typeof item === 'string') {
      result.push({ email, display_name: '', rsvp_state: 'needs_action' })
      continue
    }

    if (item && typeof item === 'object') {
      const record = item as CalendarExternalParticipant
      result.push({
        email,
        display_name: record.display_name?.trim() || '',
        rsvp_state: record.rsvp_state ?? 'needs_action'
      })
    }
  }

  return result
}

export function normalizeExternalParticipantsForApi(
  values: string[]
): CalendarExternalParticipant[] {
  return values
    .map((email) => email.trim())
    .filter(Boolean)
    .map((email) => ({ email, display_name: '', rsvp_state: 'needs_action' as const }))
}

export function externalParticipantEmails(values: unknown): string[] {
  if (!Array.isArray(values)) {
    return []
  }

  return values.map(participantEmail).filter(Boolean)
}
